<script setup lang="ts">
import { Form, Link } from "@inertiajs/vue3"
import { useTranslation } from "i18next-vue"
import PersistentLayout from "@/layouts/PersistentLayout.vue"
import Auth from "@/layouts/Auth.vue"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { sessions, passwords } from "@/routes"

defineOptions({ layout: [PersistentLayout, Auth] })

const { t } = useTranslation()
const createRoute = passwords.create()

const errs = (error: string | string[] | undefined) =>
  error ? [error].flat() : []
</script>

<template>
  <div class="flex flex-col gap-6">
    <Card>
      <CardHeader class="text-center">
        <CardTitle as="h1" class="text-xl" data-testid="page-title">
          {{ t("passwords.request.title") }}
        </CardTitle>
        <CardDescription>
          {{ t("passwords.request.description") }}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form
          :action="createRoute.url"
          :method="createRoute.method"
          #default="{ errors, processing }"
        >
          <FieldGroup>
            <Field>
              <FieldLabel for="email_address">
                {{ t("session.email_address") }}
              </FieldLabel>
              <Input
                id="email_address"
                name="email_address"
                type="email"
                autocomplete="username"
                placeholder=""
                required
                :aria-invalid="Boolean(errors.emailAddress)"
              />
              <FieldError :errors="errs(errors.emailAddress)" />
            </Field>

            <!--
              Hidden decoy so the browser recognizes this as a login form and
              offers the saved credential for the email field above.
              PasswordsController#create only reads email_address.
            -->
            <input
              type="password"
              name="password"
              autocomplete="current-password"
              class="sr-only"
              tabindex="-1"
              aria-hidden="true"
            />

            <Field>
              <Button type="submit" :disabled="processing">
                {{ t("passwords.request.submit") }}
              </Button>
              <FieldDescription class="text-center">
                <Link :href="sessions.new().url">
                  {{ t("passwords.back_to_login") }}
                </Link>
              </FieldDescription>
            </Field>
          </FieldGroup>
        </Form>
      </CardContent>
    </Card>
  </div>
</template>
